"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Building2, Star, Code, Search, FileText, Globe, Link2, Map, Crosshair, Target, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const localServices = [{
  id: "gbp",
  title: "Google Business Profile",
  subtitle: "Optimization & Verification",
  description: "Complete setup, optimization, and continuous management of your GBP listing to maximize Map Pack rankings and high-intent local call conversions.",
  features: "Map Pack Optimization, Category Selection, Geo-Photos",
  iconName: "MapPin"
}, {
  id: "citations",
  title: "Local Citation Building",
  subtitle: "NAP Consistency & Audits",
  description: "Audit and build high-authority local directory citations, fixing duplicate NAP (Name, Address, Phone) data across key search directories.",
  features: "Directory Submission, NAP Cleanup, Data Aggregators",
  iconName: "Building2"
}, {
  id: "reviews",
  title: "Review Management",
  subtitle: "Reputation Strategy",
  description: "Proactive strategies to generate authentic customer reviews, handle response workflows, and signal high local trust to search algorithms.",
  features: "Review Acquisition Setup, Response Strategy, Trust Badges",
  iconName: "Star"
}, {
  id: "schema",
  title: "Local Schema Markup",
  subtitle: "Structured Data Implementation",
  description: "Custom JSON-LD schema implementation for LocalBusiness, geo-coordinates, opening hours, and service areas to enhance search snippets.",
  features: "LocalBusiness JSON-LD, Opening Hours, Service Area Schema",
  iconName: "Code"
}, {
  id: "keywords",
  title: "Local Keyword Research",
  subtitle: "Geo-Intent & Search Volume",
  description: "In-depth identification of high-intent local search queries, geo-modified terms, and 'near me' phrases specific to your target locations.",
  features: "Geo-Query Analysis, Competitor Gap, High-Intent Mapping",
  iconName: "Search"
}, {
  id: "content",
  title: "Hyper-Local Content",
  subtitle: "Targeted Landing Pages",
  description: "Creating genuinely useful, location-specific copy and guides tailored to local audience pain points without thin content penalties.",
  features: "City-Specific Pages, Area Guides, Service Coverage Copy",
  iconName: "FileText"
}, {
  id: "onpage",
  title: "On-Page Local SEO",
  subtitle: "Geo-Targeted Meta & Tags",
  description: "Optimizing page titles, headers, image alt attributes, and embedded Google Maps for seamless local keyword alignment.",
  features: "Title & Meta Optimization, Map Embeds, Local Internal Links",
  iconName: "Globe"
}, {
  id: "linkbuilding",
  title: "Local Link Outreach",
  subtitle: "Community & Press Links",
  description: "Acquiring relevant local backlinks from regional news outlets, local business chambers, community blogs, and industry partners.",
  features: "Regional Press, Sponsorship Outreach, Local Partner Links",
  iconName: "Link2"
}, {
  id: "multilocation",
  title: "Multi-Location SEO",
  subtitle: "Franchise & Chain Growth",
  description: "Scalable local SEO architecture for businesses operating across multiple towns, regions, or physical store networks.",
  features: "Store Locator SEO, Multi-GBP Management, Region Hubs",
  iconName: "Map"
}, {
  id: "geotargeted",
  title: "Geo-Targeted UX",
  subtitle: "Conversion Optimization",
  description: "Enhancing local user experience with dynamic location callouts, click-to-call buttons, and direct local quote request forms.",
  features: "Click-To-Call UX, Local Trust Badges, Form Conversion",
  iconName: "Crosshair"
}, {
  id: "competitors",
  title: "Local Competitor Analysis",
  subtitle: "Map Pack Benchmarking",
  description: "Reverse-engineering top-ranking local competitors to uncover their citation sources, review velocity, and content strategies.",
  features: "Map Pack Benchmark, Citation Gap, Review Velocity Check",
  iconName: "Target"
}, {
  id: "tracking",
  title: "Local Rank Tracking",
  subtitle: "Grid & Geo-Location Reporting",
  description: "Transparent, geo-grid rank tracking showing exactly where your business ranks in Google Maps across specific postal codes and towns.",
  features: "Geo-Grid Rank Tracking, Monthly GBP Insights, Direct KPI Reports",
  iconName: "BarChart3"
}];
const iconMap: Record<string, React.ElementType> = {
  MapPin,
  Building2,
  Star,
  Code,
  Search,
  FileText,
  Globe,
  Link2,
  Map,
  Crosshair,
  Target,
  BarChart3
};
export default function Localservices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseoservicesgrid" data-nav="light" className="bg-background text-foreground py-20 sm:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs font-semibold tracking-wide uppercase bg-secondary text-secondary-foreground border-transparent">
            HANDS-ON LOCAL SEO EXPERTISE
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight mb-6">
            12 Targeted Local SEO Services Built for Sustainable Search Growth
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            You work directly with me, Yassine Essaadi. I personally design and execute every local SEO campaign to help your business dominate local map packs, capture high-intent regional traffic, and convert nearby customers.
          </p>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-xs sm:text-sm font-medium text-foreground/80">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
              <span>Direct Consultant Access</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
              <span>No Junior Account Managers</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
              <span>Transparent Reporting</span>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {localServices.map((service, i) => {
          const IconComponent = iconMap[service.iconName] || MapPin;
          return <motion.div key={service.id} data-index={i} variants={cardVariants} className="h-full">
                <Card className="h-full flex flex-col justify-between overflow-hidden border border-border shadow-md transition-[box-shadow,border-color] duration-300 hover:shadow-xl hover:border-primary/50 bg-card text-card-foreground rounded-xl">
                  {/* Card Top / Header */}
                  <CardHeader className="bg-primary text-primary-foreground p-6 rounded-t-xl">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2.5 bg-primary-foreground/15 rounded-lg text-primary-foreground">
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground border-transparent font-medium text-xs px-2.5 py-0.5">
                        Local SEO
                      </Badge>
                    </div>

                    <CardTitle className="text-xl font-bold font-serif text-primary-foreground leading-snug">
                      {service.title}
                    </CardTitle>

                    <CardDescription className="text-primary-foreground/80 text-xs font-medium uppercase tracking-wider mt-1">
                      {service.subtitle}
                    </CardDescription>
                  </CardHeader>

                  {/* Card Content */}
                  <CardContent className="flex-1 p-6 flex flex-col justify-between">
                    <p className="text-card-foreground/80 text-sm leading-relaxed mb-6">
                      {service.description}
                    </p>

                    <div className="pt-4 border-t border-border mt-auto">
                      <span className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">
                        Key Focus Areas
                      </span>
                      <p className="text-xs text-foreground font-medium bg-muted/60 p-2.5 rounded-md border border-border/60">
                        {service.features}
                      </p>
                    </div>
                  </CardContent>

                  {/* Card Footer / Action */}
                  <CardFooter className="px-6 pb-6 pt-0">
                    <Button asChild variant="outline" className="w-full justify-between group text-xs font-semibold hover:bg-primary hover:text-primary-foreground transition-colors border-border h-10">
                      <Link to="/ContactPage">
                        <span>Discuss {service.title}</span>
                        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom CTA Card */}
        <div className="mt-16 bg-muted/50 border border-border rounded-2xl p-8 sm:p-12 shadow-lg text-center max-w-4xl mx-auto">
          <Badge variant="outline" className="mb-4 px-3 py-1 text-xs border-primary/30 text-primary font-semibold uppercase tracking-wider">
            FREE LOCAL CONSULTATION
          </Badge>

          <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground mb-4">
            Need a Tailored Local SEO Strategy for Your Specific Location?
          </h3>

          <p className="text-muted-foreground text-sm sm:text-base max-w-2xl mx-auto mb-8 leading-relaxed">
            I will review your current Google Business Profile, citation footprint, and competitor rankings to show you actionable opportunities for local map pack dominance.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto px-8 bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm font-semibold text-sm h-11">
              <Link to="/ContactPage">
                <span>Book a Free Local SEO Consultation</span>
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto px-8 border-border hover:bg-accent hover:text-accent-foreground font-semibold text-sm h-11">
              <Link to="/CaseStudiesPage">
                <span>Explore Real Case Studies</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}